import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import Icon from 'src/components/ui/Icon/Icon';
import i18n from 'src/localization/i18n';
import { DatePicker } from 'src/components/ui/DatePicker/DatePicker';

dayjs.extend(isoWeek);

export interface PeriodRange {
  key: string;
  label: string;
  from: string;
  to: string;
}

const ISO = 'YYYY-MM-DD';
const EPOCH = '2000-01-01';

type PresetDef = { key: string; label: string; resolve: (today: Dayjs) => [Dayjs | string, Dayjs] };

const PRESETS: PresetDef[] = [
  { key: 'this_week', label: 'Diese Woche', resolve: t => [t.startOf('isoWeek'), t] },
  { key: 'last_30_days', label: '30T', resolve: t => [t.subtract(29, 'day'), t] },
  { key: 'this_month', label: 'Monat', resolve: t => [t.startOf('month'), t] },
  {
    key: 'last_month',
    label: 'Letzter Monat',
    resolve: t => [t.subtract(1, 'month').startOf('month'), t.subtract(1, 'month').endOf('month')],
  },
  { key: 'last_3_months', label: '3M', resolve: t => [t.subtract(3, 'month').add(1, 'day'), t] },
  { key: 'last_6_months', label: '6M', resolve: t => [t.subtract(6, 'month').add(1, 'day'), t] },
  { key: 'last_12_months', label: '12M', resolve: t => [t.subtract(12, 'month').add(1, 'day'), t] },
  { key: 'this_year', label: 'Jahr', resolve: t => [t.startOf('year'), t] },
  { key: 'all_time', label: 'Gesamt', resolve: t => [EPOCH, t] },
];

function toIso(value: Dayjs | string): string {
  return typeof value === 'string' ? value : value.format(ISO);
}

export function resolvePreset(key: string, today = dayjs()): PeriodRange {
  const preset = PRESETS.find(p => p.key === key) ?? PRESETS[1];
  const [from, to] = preset.resolve(today);
  return { key: preset.key, label: preset.label, from: toIso(from), to: toIso(to) };
}

export function monthRange(anchor: Dayjs): PeriodRange {
  return {
    key: `month:${anchor.format('YYYY-MM')}`,
    label: anchor.format('MMMM YYYY'),
    from: anchor.startOf('month').format(ISO),
    to: anchor.endOf('month').format(ISO),
  };
}

export function formatRange(range: PeriodRange): string {
  if (range.from === EPOCH) {
    return i18n.t('period.all_range');
  }
  const from = dayjs(range.from);
  const to = dayjs(range.to);
  if (from.isSame(to, 'day')) {
    return from.format('DD.MM.YYYY');
  }
  return `${from.format('DD.MM.YYYY')} – ${to.format('DD.MM.YYYY')}`;
}

interface PeriodBarProps {
  value: PeriodRange;
  onChange: (range: PeriodRange) => void;
}

const PeriodBar: React.FC<PeriodBarProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const anchor = React.useMemo(
    () => (value.key.startsWith('month:') ? dayjs(value.from) : dayjs(value.to)),
    [value.key, value.from, value.to],
  );
  const nextDisabled = anchor.add(1, 'month').startOf('month').isAfter(dayjs().startOf('month'));

  const stepMonth = (delta: number) => {
    const next = anchor.add(delta, 'month');
    if (next.startOf('month').isAfter(dayjs().startOf('month'))) {
      return;
    }
    onChange(monthRange(next));
  };

  const isCustom = value.key === 'custom';
  const [showCustom, setShowCustom] = React.useState(false);
  const [customFrom, setCustomFrom] = React.useState<Dayjs | null>(isCustom ? dayjs(value.from) : null);
  const [customTo, setCustomTo] = React.useState<Dayjs | null>(isCustom ? dayjs(value.to) : null);

  const openCustom = () => {
    setCustomFrom(dayjs(value.from));
    setCustomTo(dayjs(value.to));
    setShowCustom(true);
  };

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    const [from, to] = customFrom.isAfter(customTo) ? [customTo, customFrom] : [customFrom, customTo];
    const range: PeriodRange = { key: 'custom', label: '', from: from.format(ISO), to: to.format(ISO) };
    onChange({ ...range, label: formatRange(range) });
    setShowCustom(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-8">
      <div
        className="flex flex-wrap items-center gap-2 rounded-base p-2"
        style={{ background: 'var(--surface-elevated-darken)' }}>
        {PRESETS.map(preset => {
          const active = value.key === preset.key;
          return (
            <button
              type="button"
              key={preset.key}
              onClick={() => onChange(resolvePreset(preset.key))}
              className={
                active ? 'typo-text-12--bold surface-elevated text-primary shadow-sm' : 'typo-text-12 text-faded'
              }
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                background: active ? undefined : 'transparent',
              }}>
              {t(`period.presets.${preset.key}`, { defaultValue: preset.label })}
            </button>
          );
        })}
      </div>

      <div
        className="flex items-center gap-2 rounded-base p-2"
        style={{ background: 'var(--surface-elevated-darken)' }}>
        <button
          type="button"
          aria-label={t('period.prev_month')}
          onClick={() => stepMonth(-1)}
          className="flex items-center"
          style={{
            padding: '4px 6px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
            background: 'var(--surface-elevated)',
            color: 'var(--text-full)',
          }}>
          <Icon name="arrowLeft" width={16} height={16} />
        </button>
        <span
          className={value.key.startsWith('month:') ? 'typo-text-12--bold' : 'typo-text-12'}
          style={{
            minWidth: 96,
            textAlign: 'center',
            color: value.key.startsWith('month:') ? 'var(--text-full)' : 'var(--text-faded)',
          }}>
          {anchor.format('MMMM YYYY')}
        </span>
        <button
          type="button"
          aria-label={t('period.next_month')}
          disabled={nextDisabled}
          onClick={() => stepMonth(1)}
          className="flex items-center"
          style={{
            padding: '4px 6px',
            borderRadius: 6,
            border: 'none',
            cursor: nextDisabled ? 'default' : 'pointer',
            background: 'var(--surface-elevated)',
            color: 'var(--text-full)',
            opacity: nextDisabled ? 0.4 : 1,
          }}>
          <Icon name="arrowRight" width={16} height={16} />
        </button>
      </div>

      <div className="relative">
        <button
          type="button"
          onClick={() => (showCustom ? setShowCustom(false) : openCustom())}
          className={isCustom ? 'typo-text-12--bold' : 'typo-text-12'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 12px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            background: isCustom ? 'var(--surface-elevated)' : 'var(--surface-elevated-darken)',
            color: isCustom ? 'var(--primary)' : 'var(--text-faded)',
            boxShadow: isCustom ? '0 1px 2px rgba(0, 0, 0, 0.15)' : undefined,
          }}>
          <Icon name="calendar-today" width={16} height={16} />
          {isCustom ? value.label : t('period.custom_range')}
        </button>
        {showCustom && (
          <>
            <button
              type="button"
              aria-label={t('period.close')}
              onClick={() => setShowCustom(false)}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 20,
                border: 'none',
                background: 'transparent',
                cursor: 'default',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                left: 0,
                zIndex: 30,
                background: 'var(--surface-elevated)',
                borderRadius: 8,
                border: '1px solid var(--border-subtle)',
                boxShadow: 'none',
                padding: 16,
                minWidth: 240,
              }}>
              <div className="flex flex-col gap-3">
                <DatePicker
                  label={t('period.from')}
                  defaultValue={customFrom}
                  maxDate={customTo ?? dayjs()}
                  onChange={v => setCustomFrom(v ? dayjs(v) : null)}
                />
                <DatePicker
                  label={t('period.to')}
                  defaultValue={customTo}
                  minDate={customFrom ?? undefined}
                  maxDate={dayjs()}
                  onChange={v => setCustomTo(v ? dayjs(v) : null)}
                />
                <button
                  type="button"
                  onClick={applyCustom}
                  disabled={!customFrom || !customTo}
                  className="typo-text-12--bold"
                  style={{
                    marginTop: 4,
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: 'none',
                    cursor: !customFrom || !customTo ? 'default' : 'pointer',
                    background: 'var(--primary)',
                    color: '#fff',
                    opacity: !customFrom || !customTo ? 0.5 : 1,
                  }}>
                  {t('period.apply')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PeriodBar;
